/**
 * MemberView - Clean, modal-friendly member summary with tabs and actions
 *
 * Minimal header (no card borders), context menu for actions,
 * and tabs for Overview, Events, and Logs.
 */

import {
    TabView,
    TableView,
    DataView,
    ContextMenu,
    View,
    LogList,
    FormView,
    Dialog,
    Member,
    User,
    UserForms
} from 'web-mojo';
import { IncidentEventList } from 'web-mojo/admin-models';

class MemberView extends View {
    constructor(options = {}) {
        super({
            className: 'member-view',
            ...options
        });

        this.tabView = null;
        this.overviewView = null;
        this.eventsView = null;
        this.logsView = null;

        this.template = `
            <div class="member-view-container">
                <div data-container="view-header"></div>
                <div data-container="view-tabs"></div>
            </div>
        `;
    }

    async onInit() {

        this.header = new View({
            containerId: "view-header",
            template: `
            <div class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                <!-- Left: status + identity -->
                <div>
                    <div class="mb-1">
                        {{#model.is_active}}
                        <span class="text-success fw-bold">ACTIVE</span>
                        {{/model.is_active}} {{^model.is_active}}
                        <span class="text-danger fw-bold">INACTIVE</span>
                        {{/model.is_active}}
                    </div>
                    <h5 class="mb-1">
                        {{#model.user.display_name}}{{model.user.display_name}}{{/model.user.display_name}}
                        {{^model.user.display_name}}{{model.user.username}}{{/model.user.display_name}}
                    </h5>
                    <div class="text-muted small">
                        {{#model.user.username}}@{{model.user.username}}{{/model.user.username}}
                        {{#model.user.email}} • {{model.user.email}}{{/model.user.email}}
                    </div>
                    <div class="text-muted small">Group: {{model.group.name}}</div>
                </div>

                <!-- Right: quick meta + menu -->
                <div class="d-flex align-items-start gap-3">
                    <div class="text-end">
                        <div class="text-muted small">Member Since</div>
                        <div class="fw-medium">{{model.created|datetime_tz}}</div>
                        {{#model.user.last_activity}}
                        <div class="text-muted small mt-2">Last Activity</div>
                        <div class="fw-medium">{{model.user.last_activity|relative}}</div>
                        {{/model.user.last_activity}}
                    </div>
                    <div data-container="view-context-menu"></div>
                </div>
            </div>`
        });
        this.header.setModel(this.model);
        this.addChild(this.header);
        // Overview tab (compact)
        this.overviewView = new DataView({
            model: this.model,
            className: 'p-3',
            showEmptyValues: true,
            fields: [
                // Membership
                { name: 'is_active|yesno', label: 'Active', type: 'text', colSize: 6 },
                { name: 'created|datetime_tz', label: 'Member Since', type: 'text', colSize: 6 },
                { name: 'modified|datetime_tz', label: 'Last Updated', type: 'text', colSize: 6 },

                // User
                { name: 'user.display_name', label: 'Name', type: 'text', colSize: 6 },
                { name: 'user.username', label: 'Username', type: 'text', colSize: 6 },
                { name: 'user.email', label: 'Email', type: 'text', colSize: 6 },
                { name: 'user.last_login|datetime_tz', label: 'Last Login', type: 'text', colSize: 6 },
                { name: 'user.last_activity|datetime_tz', label: 'Last Activity', type: 'text', colSize: 6 },

                // Group
                { name: 'group.name', label: 'Group', type: 'text', colSize: 6 },
                { name: 'group.kind|uppercase', label: 'Group Type', type: 'text', colSize: 6 }
            ]
        });

        this.permsView = new FormView({
            fields: Member.PERMISSION_FIELDS,
            model: this.model,
            permissions: ["manage_group", "manage_users", "manage_groups"],
            autosaveModelField: true
        });

        // Events (user events)
        const eventsCollection = new IncidentEventList({
            params: {
                size: 5,
                model_name: "account.User",
                model_id: this.model.get("user.id")
            }
        });
        this.eventsView = new TableView({
            collection: eventsCollection,
            fontSize: 'xs',
            fetchOnMount: true,
            permissions: ["manage_users", "manage_groups", "view_logs"],
            hideActivePillNames: ['model_name', 'model_id'],
            columns: [
                { key: 'id', label: 'ID', sortable: true, class: 'col-1' },
                { key: 'created', label: 'Date', formatter: 'datetime', sortable: true, class: 'col-2' },
                { key: 'category|badge', label: 'Category' },
                { key: 'title', label: 'Title' }
            ]
        });

        // Logs (user logs)
        const logsCollection = new LogList({
            params: {
                size: 5,
                model_name: "account.User",
                model_id: this.model.get("user.id")
            }
        });
        this.logsView = new TableView({
            collection: logsCollection,
            permissions: 'view_logs',
            fetchOnMount: true,
            fontSize: 'xs',
            permissions: ["manage_users", "manage_groups", "view_logs"],
            hideActivePillNames: ['model_name', 'model_id'],
            columns: [
                {
                    key: 'created',
                    label: 'Timestamp',
                    sortable: true,
                    formatter: "epoch|datetime",
                    filter: {
                        name: "created",
                        type: 'daterange',
                        startName: 'dr_start',
                        endName: 'dr_end',
                        fieldName: 'dr_field',
                        label: 'Date Range',
                        format: 'YYYY-MM-DD',
                        displayFormat: 'MMM DD, YYYY',
                        separator: ' to '
                    }
                },
                {
                    key: 'level',
                    label: 'Level',
                    sortable: true,
                    filter: {
                        type: 'select',
                        options: [
                            { value: 'info', label: 'Info' },
                            { value: 'warning', label: 'Warning' },
                            { value: 'error', label: 'Error' }
                        ]
                    }
                },
                { key: 'kind', label: 'Kind', filter: { type: 'text' } },
                { name: 'log', label: 'Log' }
            ]
        });

        // Tabs
        this.tabView = new TabView({
            tabs: {
                'Overview': this.overviewView,
                "Permissions": this.permsView,
                'Events': this.eventsView,
                'Logs': this.logsView
            },
            activeTab: 'Overview',
            containerId: 'view-tabs',
            enableResponsive: true,
            dropdownStyle: "select",
            minWidth: 300
        });
        this.addChild(this.tabView);

        // Context menu for member actions
        const contextMenu = new ContextMenu({
            containerId: 'view-context-menu',
            className: "context-menu-view header-menu-absolute",
            context: this.model,
            config: {
                icon: 'bi-three-dots-vertical',
                items: [
                    this.model.get('is_active')
                        ? { label: 'Disable Member', action: 'disable-member', icon: 'bi-person-dash' }
                        : { label: 'Enable Member', action: 'enable-member', icon: 'bi-person-check' },
                    { type: 'divider' },
                    { label: 'Edit', action: 'edit', icon: 'bi-pencil' }
                ]
            }
        });
        this.addChild(contextMenu);
    }

    async onActionDeactivateMember() {
        await this.getApp().showLoading();
        const resp = await this.model.save({ is_active: false });
        await this.getApp().hideLoading();
        if (resp && resp.success) {
            this.getApp().toast.success("Member disabled");
            this.render();
        } else {
            this.getApp().toast.error("Failed to disable member");
        }
        return true;
    }

    async onActionActivateMember() {
        await this.getApp().showLoading();
        const resp = await this.model.save({ is_active: true });
        await this.getApp().hideLoading();
        if (resp && resp.success) {
            this.getApp().toast.success("Member enabled");
            this.render();
        } else {
            this.getApp().toast.error("Failed to enable member");
        }
        return true;
    }

    async onActionEdit() {
        const result = await this.getApp().showModelForm({
          model: this.model,
          fields: this.model.constructor.EDIT_FORM.fields
        });
    }

    async onActionEnableMember() {
        const res = await Dialog.confirm("Are you sure you want to enable this member?");
        if (res) {
            await this.model.save({ is_active: true });
            this.getApp().toast.success("Member enabled");
        } else {
            this.getApp().toast.error("Member enable failed");
        }
    }

    async onActionDisableMember() {
        const res = await Dialog.confirm("Are you sure you want to disable this member?");
        if (res) {
            await this.model.save({ is_active: false });
            this.getApp().toast.success("Member disabled");
        } else {
            this.getApp().toast.error("Member disabled failed");
        }
    }

    _onModelChange() {
      // do nothing, we do not want model changes to render this entire view
    }

    static create(options = {}) {
        return new MemberView(options);
    }
}

Member.VIEW_CLASS = MemberView;

Member.BASE_PERMISSIONS = [
    { name: "manage_cam_devices", label: "Manage Devices" },
    { name: "view_members", label: "View Members" },
    { name: "view_metrics", label: "View Reports" },
    // { name: "billing", label: "Billing" },
    { name: "manage_group", label: "Group Admin", permissions: ["sys.manage_groups", "sys.manage_users"] },

];

Member.BASE_NOTIFICATIONS = [
    {
        name:"metadata.notify.group_left_open",
        label:"Mobile Left Open",
        help: "The system will send a mobile notification to the user any time a lock in this group is left open.",
        type:"toggle",
        columns: 6
    },
    {
        name:"metadata.notify.email_left_open",
        label:"Email Left Open",
        help: "The system will email the user any time a lock is left open.",
        type:"toggle",
        columns: 6
    },
    {
        name:"metadata.notify.left_open_summary",
        label:"Left Open Summary",
        help: "The system will email a daily summary of locks left opened.",
        type:"toggle",
        columns: 6
    },
    {
        name:"metadata.notify.no_mobile_sync",
        label:"No Mobile Sync",
        help: "This user will receive no mobile push notifications (including lock credential syncing).",
        type:"toggle",
        columns: 6
    },
    {
        name:"metadata.notify.ignore_left_open",
        label:"Ignore Left Open",
        help: "System will not send 'Left Open' notifications to this user on mobile.",
        type:"toggle",
        columns: 6
    },
    {
        name:"metadata.notify.ignore_on_portal",
        label:"Ignore Notifications in Portal",
        help: "This user will not see portal notifications.",
        type:"toggle",
        columns: 6
    }

];

Member.PAGE_PERMISSIONS = {
    "devices": ["view_payment_devices", "manage_payment_devices", "manage_group", "manage_groups"],
    "txs": ["view_pos_txs", "manage_group", "manage_groups"],
    "reports": ["view_metrics", "manage_group", "manage_groups"],
    "employees": ["view_pos_employees", "manage_pos_employees", "manage_group", "manage_groups"],
    "members": ["view_members", "manage_group", "manage_groups"],
    "tickets": ["view_tickets", "manage_group", "manage_groups"],
    "atm": ["view_atm_txs", "manage_atm_config", "manage_group", "manage_groups"],
};

Member.PERMISSION_FIELDS = [
    {"type":"header", text: "Basic Permissions", level:5},
    ...Member.BASE_PERMISSIONS.map(permission => ({
        name: `permissions.${permission.name}`,
        type: 'switch',
        label: permission.label,
        columns: 4
    })),
    {"type":"heading", text: "Notifications", level:5, permissions: ["sys.manage_groups", "sys.manage_users"]},
    ...Member.BASE_NOTIFICATIONS
];

// how to add PERMISSIONS

UserForms.permissions.fields = [
    {"type":"header", text: "System Permissions", level:5},
    ...User.PERMISSIONS.map(permission => ({
        name: `permissions.${permission.name}`,
        type: 'switch',
        label: permission.label,
        columns: 4
    })),
    ...Member.PERMISSION_FIELDS
];

User.PERMISSION_FIELDS = UserForms.permissions.fields;

export default MemberView;
